<script setup>
/**
 * SzSenderBadge — pill com nome/avatar do remetente dentro do balão.
 *
 * Comportamento por tipo:
 *  - OUTGOING (agente respondendo): mostra agente que enviou.
 *  - INCOMING numa conversa de GRUPO (@g.us): mostra o NOME do CONTATO
 *    individual extraído do content (`*Nome*:\n...`), avatar com inicial
 *    do nome, e o número embaixo. Pill inteira clicável → abre o modal
 *    "Nova mensagem" pré-preenchido via window.__szOpenNewMessage().
 *  - INCOMING numa conversa individual: comportamento original (mostra
 *    o sender do payload, normalmente o próprio contato).
 *
 * Marker: SZ_SENDER_BADGE_GROUP_v2_20260520
 *
 * SZ_PARTICIPANT_COLOR_PILL_20260527 (Sadraque #363):
 *  - Em INCOMING dentro de conversa de GRUPO, o pill do participante
 *    recebe uma COR ÚNICA baseada no JID/phone (hash determinístico).
 *  - O telefone sai de dentro do pill e fica OUTSIDE da bolha, à direita,
 *    via position: absolute relativo ao próprio pill. Bolha não tem
 *    overflow:hidden, então o telefone "vaza" pra fora visualmente.
 */
import { computed } from 'vue';
import { useStore } from 'vuex';
import { szAgentColors } from './szAgentPalette.js';

const props = defineProps({
  sender: { type: Object, default: null },
  messageType: { type: Number, default: 0 },
  isPrivate: { type: Boolean, default: false },
  content: { type: String, default: '' },
  contentAttributes: { type: [Object, String], default: () => ({}) },
});

const store = useStore();

const isIncoming = computed(() => props.messageType === 0);
const isOutgoing = computed(() => props.messageType === 1);

// Detecta se a conversa atual é grupo (identifier termina com @g.us)
const isGroupConversation = computed(() => {
  const id =
    store.getters?.getSelectedChat?.meta?.sender?.identifier || '';
  return String(id).endsWith('@g.us');
});

// content_attributes pode chegar como string em casos defensivos
const attrsObj = computed(() => {
  const v = props.contentAttributes;
  if (!v) return {};
  if (typeof v === 'string') {
    try { return JSON.parse(v) || {}; } catch (e) { return {}; }
  }
  return v;
});

// Chatwoot frontend converte snake_case do backend pra camelCase
const senderJid = computed(
  () => attrsObj.value?.senderJid || attrsObj.value?.sender_jid || ''
);

const phoneDigits = computed(() => {
  const jid = String(senderJid.value || '');
  if (!jid) return '';
  return (jid.split('@')[0] || '').replace(/\D/g, '');
});

const nameFromContent = computed(() => {
  const c = String(props.content || '');
  const m = c.match(/^\*([^*]+)\*\s*:\s*\n?/);
  return m ? m[1].trim() : '';
});

// Modo "group sender": só ativa em INCOMING dentro de conversa de grupo
// E com dados suficientes (phone ou nome) pra valer a pena renderizar
const useGroupSender = computed(
  () =>
    isIncoming.value &&
    isGroupConversation.value &&
    (!!phoneDigits.value || !!nameFromContent.value)
);

// ── Group sender display ──
const groupSenderName = computed(
  () => nameFromContent.value || phoneDigits.value || 'Participante'
);

const groupSenderInitials = computed(() => {
  const src = nameFromContent.value || '';
  if (!src) return '#';
  return src
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

// SZ_PARTICIPANT_COLOR_PILL_20260527 — cor ÚNICA por participante.
// Hash baseado em phoneDigits (estável) com fallback pra hash do nome.
function _hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}
const participantColor = computed(() => {
  if (!useGroupSender.value) return null;
  const key = phoneDigits.value || nameFromContent.value || '0';
  // Reaproveita o palette do szAgentColors usando o hash da string como "id"
  return szAgentColors(_hashStr(key));
});
const participantStyleVars = computed(() => {
  if (!participantColor.value) return null;
  return {
    '--sz-participant-bg': participantColor.value.bg,
    '--sz-participant-fg': participantColor.value.fg,
    '--sz-participant-bg-dark': participantColor.value.bgDark,
    '--sz-participant-fg-dark': participantColor.value.fgDark,
  };
});

// Formatação BR do telefone (`+55 (84) 98123-2812`)
const participantPhoneFormatted = computed(() => {
  const d = phoneDigits.value;
  if (!d) return '';
  let local = d;
  let cc = '';
  if (d.startsWith('55') && d.length >= 12) {
    cc = '+55 ';
    local = d.slice(2);
  } else if (d.length > 11) {
    cc = '+' + d.slice(0, d.length - 11) + ' ';
    local = d.slice(-11);
  }
  if (local.length === 11) {
    return `${cc}(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `${cc}(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return d;
});

// ── Fallback (comportamento original — agente/sender do payload) ──
const senderName = computed(() => {
  if (!props.sender) return '';
  return props.sender.name || props.sender.available_name || '';
});

const senderInitials = computed(() => {
  if (!senderName.value) return '';
  return senderName.value
    .split(' ')
    .map(w => w.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

const senderAvatar = computed(() => {
  if (!props.sender) return '';
  return props.sender.thumbnail || props.sender.avatar_url || '';
});

const isBot = computed(() => {
  if (!props.sender) return true;
  return props.sender.type === 'agent_bot';
});

const isVisible = computed(() => {
  if (useGroupSender.value) return true;
  return senderName.value && !isBot.value;
});

async function handleGroupSenderClick() {
  const phone = phoneDigits.value;
  if (!phone) return;
  const name = nameFromContent.value;
  const currentInboxId = store.getters?.getSelectedChat?.inbox_id || null;
  const accountId = store.getters?.getCurrentAccountId;
  const token = store.getters?.getCurrentUser?.access_token;

  // SZ_GROUP_PILL_v2_20260522: caminho rápido — busca conversa existente
  // no MESMO inbox do grupo. Se achar, navega direto (sem modal).
  // Se contato existir mas sem conversa, passa pro modal pra ele pular etapa 1.
  let prefillContact = null;
  if (accountId && token && currentInboxId) {
    try {
      const sUrl = `/api/v1/accounts/${accountId}/contacts/search?q=${encodeURIComponent(phone)}&include_contacts=true`;
      const sResp = await fetch(sUrl, { headers: { api_access_token: token } });
      const sData = await sResp.json();
      const contacts = sData.payload || [];
      const contact = contacts.find(c => {
        const ph = String(c.phone_number || '').replace(/\D/g, '');
        const id = String(c.identifier || '');
        return ph.endsWith(phone) || ph === phone || id.includes(phone);
      });
      if (contact && contact.id) {
        prefillContact = contact;
        const cUrl = `/api/v1/accounts/${accountId}/contacts/${contact.id}/conversations`;
        const cResp = await fetch(cUrl, { headers: { api_access_token: token } });
        const cData = await cResp.json();
        const convs = cData.payload || [];
        const matched = convs.filter(cv => cv.inbox_id === currentInboxId);
        if (matched.length > 0) {
          // Qualquer status (open/pending/snoozed/resolved). Mais recente vence.
          matched.sort((a, b) =>
            (b.last_activity_at || b.created_at || 0) -
            (a.last_activity_at || a.created_at || 0)
          );
          const conv = matched[0];
          const path = `/app/accounts/${accountId}/conversations/${conv.id}`;
          const routerEl = document.querySelector('#app');
          const router = routerEl?.__vue_app__?.config?.globalProperties?.$router;
          if (router) {
            router.push(path).catch(() => { window.location.href = path; });
          } else {
            window.location.href = path;
          }
          return;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SzSenderBadge] lookup error, fallback to modal:', e);
    }
  }

  // Fallback: sem conversa no inbox do grupo → modal só com canal.
  // Passa o contato (se achado) via window pro modal pular a etapa de busca.
  if (prefillContact) {
    try { window.__szPrefillContact = prefillContact; } catch (e) {}
  }
  if (typeof window.__szOpenNewMessage === 'function') {
    try {
      window.__szOpenNewMessage(phone, name, { channelOnly: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SzSenderBadge] __szOpenNewMessage error:', e);
    }
  }
}
</script>

<template>
  <!-- Modo GRUPO (incoming): pill colorido + telefone na MESMA linha como flex row.
       Phone como sibling do pill (não filho), pra não quebrar com hover/quoted/etc. -->
  <div v-if="useGroupSender" class="sz-participant-row">
    <button
      type="button"
      class="sz-sender-badge sz-sender-badge--group sz-sender-badge--group-colored"
      :style="participantStyleVars"
      :data-sz-participant-color="participantColor && participantColor.name"
      :title="phoneDigits ? `Abrir nova mensagem para ${phoneDigits}` : ''"
      @click.stop="handleGroupSenderClick"
    >
      <span class="sz-sender-initials sz-sender-initials--group sz-sender-initials--group-colored">{{ groupSenderInitials }}</span>
      <span class="sz-sender-name sz-sender-name--group sz-sender-name--group-colored">{{ groupSenderName }}</span>
    </button>
    <span
      v-if="participantPhoneFormatted"
      class="sz-sender-phone sz-sender-phone--inline"
    >{{ participantPhoneFormatted }}</span>
  </div>

  <!-- Modo padrão (outgoing, ou incoming de conversa individual) -->
  <div v-else-if="isVisible" class="sz-sender-badge">
    <span v-if="senderAvatar" class="sz-sender-avatar">
      <img :src="senderAvatar" :alt="senderInitials" />
    </span>
    <span v-else class="sz-sender-initials">{{ senderInitials }}</span>
    <span class="sz-sender-name">{{ senderName }}</span>
  </div>
</template>

<style>
/* SZ_SENDER_BADGE_GROUP_v2_20260520 — pill clicável de remetente de grupo.
   Cores alinhadas com o sz-sender-badge original (solarz_override.css):
   bg #EBEBEB, texto #1C2024. Avatar com cor própria pra contrastar com o bg. */
.sz-sender-badge--group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #EBEBEB;
  border: 0;
  border-radius: 20px;
  padding: 0 10px 0 2px;
  margin: 0 0 2px 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #1C2024;
  line-height: 1;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s ease, opacity 0.12s ease;
}

.dark .sz-sender-badge--group {
  background: #00000024;
  color: #EDEEEF;
}

.sz-sender-badge--group:hover {
  background: #DEDEDE;
}

.dark .sz-sender-badge--group:hover {
  background: #00000040;
}

.sz-sender-badge--group:hover .sz-sender-name--group {
  text-decoration: underline;
}

.sz-sender-initials--group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background: #60646C;
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  line-height: 1;
}

.dark .sz-sender-initials--group {
  background: #4A4D55;
  color: #FFFFFF;
}

.sz-sender-group-meta {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  line-height: 1;
  min-width: 0;
}

.sz-sender-name--group {
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sz-sender-phone {
  color: inherit;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.65;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* SZ_PARTICIPANT_COLOR_PILL_20260527 — Sadraque #363
   Sobrescreve `.sz-sender-badge--group` quando aplica cor por participante.
   Mantém posição (dentro do balão, top-left). Telefone vira `.sz-sender-phone--external`
   posicionado absoluto relativo à BOLHA (left-bubble/right-bubble), no canto sup-direito. */
.sz-sender-badge--group-colored {
  background: var(--sz-participant-bg, #F3EEF2) !important;
  color: var(--sz-participant-fg, #81336D) !important;
  padding: 2px 8px 2px 2px !important;
  gap: 4px !important;
  /* position: static (default) — phone NÃO se posiciona relativo ao pill */
}
.sz-sender-badge--group-colored:hover {
  background: var(--sz-participant-bg, #F3EEF2) !important;
  filter: brightness(0.96);
}
.dark .sz-sender-badge--group-colored {
  background: var(--sz-participant-bg-dark, rgba(129, 51, 109, 0.24)) !important;
  color: var(--sz-participant-fg-dark, #E5B5D7) !important;
}
.dark .sz-sender-badge--group-colored:hover {
  filter: brightness(1.1);
}
/* Avatar/iniciais do participante: usa fg como bg pra contraste */
.sz-sender-initials--group-colored {
  background: var(--sz-participant-fg, #81336D) !important;
  color: #FFFFFF !important;
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
  font-size: 8px !important;
}
.dark .sz-sender-initials--group-colored {
  background: var(--sz-participant-fg-dark, #E5B5D7) !important;
  color: #1A1A1A !important;
}
/* Nome do participante: cor da palette */
.sz-sender-name--group-colored {
  color: var(--sz-participant-fg, #81336D) !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 100% !important;
  letter-spacing: 0 !important;
  max-width: 220px;
}
.dark .sz-sender-name--group-colored {
  color: var(--sz-participant-fg-dark, #E5B5D7) !important;
}
.sz-sender-badge--group-colored:hover .sz-sender-name--group-colored {
  text-decoration: none;
}

/* Row com [pill | phone] usando flex (não absolute) — evita quebras com
   hover do title-tooltip e overlap com quoted-reply preview. */
.sz-participant-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0 0 4px 0;
}
.sz-sender-phone--inline {
  white-space: nowrap;
  color: #4B5563;
  font-size: 11px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  opacity: 0.85;
  flex-shrink: 0;
  padding-right: 2px;
}
.dark .sz-sender-phone--inline {
  color: #9CA3AF;
}

/* Min-width fixo na bolha quando o row de participante existe — evita que
   bolhas curtas (ex: "iniciar as 9:30h") comprimam a row de pill+phone. */
#app .left-bubble:has(.sz-participant-row),
#app .right-bubble:has(.sz-participant-row) {
  min-width: 320px;
}
</style>
